import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTemplates } from '../redux/globalDataSlice';
import { useToaster } from '../context/ToasterContext';
import api from '../api/axios';
import { schema } from '../pages/templateConstants';

export function useTemplateForm(id) {
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { toaster } = useToaster();
  const dispatch = useDispatch();

  const activeAccountId = useSelector(s => s.globalData.activeAccountId);
  const { policies, dataLoading } = useSelector(s => s.globalData);

  const [loading, setLoading] = useState(true);
  const [templateData, setTemplateData] = useState(null);
  const [policiesOptions, setPoliciesOptions] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      notes: '',
      inboundUrl: '',
      statusUrl: '',
      routingStrategy: '',
      ttl: '',
      route: true,
      sticky: true,
      retryAlternateVendor: true,
      redact: true,
      policyId: '',
      senders: [],
    },
  });

  // Build policy options when policies change
  useEffect(() => {
    if (Array.isArray(policies)) {
      setPoliciesOptions(policies.map(p => ({ value: p.id, label: p.name })));
    }
  }, [policies]);

  // For create mode, set hidden accountId once we have it
  useEffect(() => {
    if (!isEditMode && activeAccountId) {
      setValue('accountId', activeAccountId, { shouldDirty: false, shouldValidate: false });
    }
  }, [isEditMode, activeAccountId, setValue]);

  // Fetch template (edit mode only)
  const fetchTemplateData = useCallback(async () => {
    try {
      const response = await api.get(`/mvapi/accounts/${activeAccountId}/templates/${id}`);
      setTemplateData(response.data);
    } catch (error) {
      console.error('Fetch template error:', error);
      toaster.error('Failed to load template: ' + error);
    } finally {
      setLoading(false);
    }
  }, [id, activeAccountId, toaster]);

  useEffect(() => {
    if (isEditMode) {
      fetchTemplateData();
    } else {
      setLoading(false);
    }
  }, [isEditMode, fetchTemplateData]);

  // Fill form when editing once data + options are ready
  useEffect(() => {
    if (isEditMode && templateData) {
      const policyExists = policiesOptions.some(opt => opt.value === templateData.policyId);
      reset({
        ...templateData,
        policyId: policyExists ? templateData.policyId : '',
        accountId: templateData.accountId || activeAccountId || '',
        senders: Array.isArray(templateData.senders) ? templateData.senders : [],
      });
    }
  }, [isEditMode, templateData, policiesOptions, reset, activeAccountId]);

  const onSubmit = async (data) => {
    try {
      const accountId = data.accountId || activeAccountId;
      if (!accountId) {
        toaster.error('No account selected. Please choose an account first.');
        return;
      }

      const requestData = { ...data, accountId };

      if (isEditMode) {
        requestData.id = id;
        await api.put(`/mvapi/accounts/${activeAccountId}/templates/${id}`, requestData);
        toaster.success('Template updated successfully!');
      } else {
        await api.post(`/mvapi/accounts/${activeAccountId}/templates`, requestData);
        toaster.success('Template created successfully!');
      }

      await dispatch(fetchTemplates(accountId));
      navigate('/templates');
    } catch (error) {
      console.error('Error:', error);
      toaster.error('Something went wrong. Please try again.');
    }
  };

  return {
    // form
    register,
    handleSubmit,
    errors,
    isSubmitting,
    setValue,
    watch,
    // data
    templateData,
    loading,
    policiesOptions,
    activeAccountId,
    dataLoading,
    isEditMode,
    // actions
    onSubmit,
  };
}
